import React, { useState, useEffect, useCallback } from 'react';
import { 
  HardDrive, FolderOpen, Image, File, ChevronRight, 
  ArrowLeft, Search, Check, X, RefreshCw, Unplug, Link2
} from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';

const GoogleDrivePicker = ({ onSelect, onClose, imagesOnly = true }) => {
  const [connected, setConnected] = useState(false);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [files, setFiles] = useState([]);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [folderHistory, setFolderHistory] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);

  // Check connection status
  const checkStatus = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/google-drive/status');
      setConnected(res.data.connected);
      setEmail(res.data.email || '');
      
      if (res.data.connected) {
        loadFiles();
      }
    } catch (err) {
      console.error('Error checking drive status:', err);
      setConnected(false);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    checkStatus();
  }, [checkStatus]);

  // Connect to Google Drive
  const handleConnect = async () => {
    try {
      const res = await api.get('/google-drive/connect');
      if (res.data.authorization_url) {
        window.location.href = res.data.authorization_url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to connect to Google Drive');
    }
  };

  // Disconnect from Google Drive
  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect Google Drive?')) return;
    try {
      await api.delete('/google-drive/disconnect');
      setConnected(false);
      setEmail('');
      setFiles([]);
      toast.success('Google Drive disconnected');
    } catch (err) {
      toast.error('Failed to disconnect');
    }
  };

  // Load files from current folder
  const loadFiles = async (folderId = null) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (folderId) params.append('folder_id', folderId);
      if (imagesOnly) params.append('images_only', 'true');
      
      const res = await api.get(`/google-drive/files?${params.toString()}`);
      setFiles(res.data);
      setCurrentFolder(folderId);
    } catch (err) {
      console.error('Error loading files:', err);
      toast.error('Failed to load files');
    }
    setLoading(false);
  };

  // Search files
  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      loadFiles(currentFolder);
      return;
    }
    
    setLoading(true);
    try {
      const params = new URLSearchParams({
        query: searchQuery,
        images_only: imagesOnly.toString()
      });
      
      const res = await api.get(`/google-drive/search?${params.toString()}`);
      setFiles(res.data);
    } catch (err) {
      toast.error('Search failed');
    }
    setLoading(false);
  };

  // Navigate to folder
  const openFolder = (folder) => {
    setFolderHistory([...folderHistory, { id: currentFolder, name: 'Previous' }]);
    loadFiles(folder.id);
  };

  // Go back
  const goBack = () => {
    if (folderHistory.length === 0) {
      loadFiles(null);
      return;
    }
    const prevFolder = folderHistory[folderHistory.length - 1];
    setFolderHistory(folderHistory.slice(0, -1));
    loadFiles(prevFolder.id);
  };

  // Toggle file selection
  const toggleSelect = (file) => {
    if (selectedFiles.find(f => f.id === file.id)) {
      setSelectedFiles(selectedFiles.filter(f => f.id !== file.id));
    } else {
      setSelectedFiles([...selectedFiles, file]);
    }
  };

  // Get file URL and confirm selection
  const handleConfirmSelection = async () => {
    if (selectedFiles.length === 0) {
      toast.error('Select at least one file');
      return;
    }

    setLoading(true);
    try {
      const urls = await Promise.all(
        selectedFiles.map(async (file) => {
          const res = await api.get(`/google-drive/file/${file.id}/url`);
          return {
            ...file,
            url: res.data.url,
            name: res.data.name
          };
        })
      );
      
      onSelect(urls);
      toast.success(`${urls.length} file(s) selected`);
    } catch (err) {
      toast.error('Failed to get file URLs');
    }
    setLoading(false);
  };

  // Get file icon
  const getFileIcon = (mimeType) => {
    if (mimeType === 'application/vnd.google-apps.folder') {
      return <FolderOpen className="w-8 h-8 text-yellow-400" />;
    }
    if (mimeType?.startsWith('image/')) {
      return <Image className="w-8 h-8 text-blue-400" />;
    }
    return <File className="w-8 h-8 text-gray-400" />;
  };

  // Check if file is a folder
  const isFolder = (file) => file.mimeType === 'application/vnd.google-apps.folder';

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 rounded-2xl w-full max-w-3xl max-h-[80vh] overflow-hidden border border-gray-700 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-700 bg-gray-800">
          <div className="flex items-center gap-3">
            <HardDrive className="w-6 h-6 text-green-400" />
            <div>
              <h2 className="text-lg font-semibold text-white">Google Drive</h2>
              {email && <p className="text-xs text-gray-400">{email}</p>}
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          {loading && !connected ? (
            <div className="flex items-center justify-center py-20">
              <RefreshCw className="w-8 h-8 animate-spin text-green-400" />
            </div>
          ) : !connected ? (
            /* Not Connected State */
            <div className="text-center py-16">
              <HardDrive className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">Connect Google Drive</h3>
              <p className="text-gray-400 mb-6 max-w-md mx-auto">
                Connect your Google Drive to browse and select images directly from your cloud storage.
              </p>
              <button
                onClick={handleConnect}
                className="inline-flex items-center gap-2 px-6 py-3 bg-green-500 text-white font-semibold rounded-lg hover:bg-green-600 transition-colors"
              >
                <Link2 className="w-5 h-5" />
                Connect Google Drive
              </button>
            </div>
          ) : (
            /* Connected State - File Browser */
            <div className="space-y-4">
              {/* Search & Navigation */}
              <div className="flex items-center gap-3">
                {(currentFolder || folderHistory.length > 0) && (
                  <button
                    onClick={goBack}
                    className="p-2 bg-gray-800 rounded-lg hover:bg-gray-700 text-white"
                  >
                    <ArrowLeft className="w-5 h-5" />
                  </button>
                )}
                
                <div className="flex-1 flex items-center gap-2">
                  <input
                    type="text"
                    placeholder="Search files..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    onKeyPress={e => e.key === 'Enter' && handleSearch()}
                    className="flex-1 bg-gray-800 border border-gray-600 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-green-500 outline-none"
                  />
                  <button
                    onClick={handleSearch}
                    className="p-2 bg-green-500 rounded-lg hover:bg-green-600 text-white"
                  >
                    <Search className="w-5 h-5" />
                  </button>
                </div>

                <button
                  onClick={() => loadFiles(currentFolder)}
                  className="p-2 bg-gray-800 rounded-lg hover:bg-gray-700 text-white"
                  title="Refresh"
                >
                  <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
                </button>

                <button
                  onClick={handleDisconnect}
                  className="p-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30"
                  title="Disconnect"
                >
                  <Unplug className="w-5 h-5" />
                </button>
              </div>

              {/* File Grid */}
              <div className="max-h-[400px] overflow-y-auto">
                {loading ? (
                  <div className="flex items-center justify-center py-20">
                    <RefreshCw className="w-8 h-8 animate-spin text-green-400" />
                  </div>
                ) : files.length === 0 ? (
                  <div className="text-center py-16 text-gray-500">
                    <FolderOpen className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No {imagesOnly ? 'images' : 'files'} found</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {files.map(file => {
                      const selected = selectedFiles.find(f => f.id === file.id);
                      const folder = isFolder(file);
                      
                      return (
                        <div
                          key={file.id}
                          onClick={() => folder ? openFolder(file) : toggleSelect(file)}
                          className={`relative p-3 rounded-lg cursor-pointer transition-all ${
                            selected
                              ? 'bg-green-500/20 border-2 border-green-500'
                              : 'bg-gray-800 border-2 border-transparent hover:border-gray-600'
                          }`}
                        >
                          {/* Thumbnail or Icon */}
                          <div className="aspect-square flex items-center justify-center bg-gray-900 rounded-lg mb-2 overflow-hidden">
                            {file.thumbnailLink ? (
                              <img
                                src={file.thumbnailLink}
                                alt={file.name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              getFileIcon(file.mimeType)
                            )}
                          </div>
                          
                          {/* File Name */}
                          <p className="text-xs text-white truncate" title={file.name}>
                            {file.name}
                          </p>
                          
                          {/* Folder Indicator */}
                          {folder && (
                            <ChevronRight className="absolute top-3 right-3 w-4 h-4 text-yellow-400" />
                          )}
                          
                          {/* Selection Checkmark */}
                          {selected && (
                            <div className="absolute top-2 right-2 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                              <Check className="w-4 h-4 text-white" />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Selection Footer */}
              {selectedFiles.length > 0 && (
                <div className="flex items-center justify-between pt-4 border-t border-gray-700">
                  <p className="text-gray-400">
                    {selectedFiles.length} file{selectedFiles.length > 1 ? 's' : ''} selected
                  </p>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setSelectedFiles([])}
                      className="px-4 py-2 text-gray-400 hover:text-white"
                    >
                      Clear
                    </button>
                    <button
                      onClick={handleConfirmSelection}
                      disabled={loading}
                      className="px-6 py-2 bg-green-500 text-white font-semibold rounded-lg hover:bg-green-600 disabled:opacity-50 flex items-center gap-2"
                    >
                      {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                      Use Selected
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GoogleDrivePicker;
